import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import Content from "../components/content";
import Loader from "../components/loader";
import CurrentSubscription, { TooltipContent, PaymentLink } from "../components/current_subscription";

import { useImpersonation } from "../global/impersonation";
import { formatValueToCsvXlsx } from "../global/formatValueToCsvXlsx";
import {
    Button,
    TextField,
    Box,
    Collapse,
    CircularProgress,
    IconButton,
    Menu,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import MaskedInput from "react-text-mask";
import createNumberMask from "text-mask-addons/dist/createNumberMask";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";

import Api from "../libs/api";
import Storage from "../libs/storage";
import Helper from "../libs/helper";

import Edit from "../components/edit_cat_proj";
import AddCategory from "../components/add_category";
import AddProject from "../components/add_project";
import ErrorModal from "../components/error_modal";
import PlanCard from "../components/plan_card";
import BasicTitle from "../components/title";

const Holder = styled.div`
    display: flex;
    height: 100%;
    width: 100%;
    overflow: auto;
`;

const Grid = styled.div`
    margin-top: 20px;
`;

const ImageIcon = styled.img`
    width: 20px;
    height: 20px;
    margin-right: 8px;
    vertical-align: middle;
    color: #57c4c2;
`;

const SubTitle = styled.div`
    font-family: "Montserrat";
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: #181122;
    text-transform: uppercase;
    width: 100%;
    margin-bottom: 5px;
`;

const Field = styled.div`
    display: flex;
    height: 100%;
`;
const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-right: 10px;
`;

const CenteredFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100px;
    height: 100px;
`;

const View = styled.div`
    display: flex;
    flex-direction: row;
`;

const ButtonAdd = styled(Button)`
    && {
        padding: 12px 16px;
        font-family: "Montserrat";
        font-weight: 600;
        font-size: 14px;
        line-height: 20px;
        color: #181122;
        text-transform: uppercase;
        background: #57c4c2;
        display: flex;
        gap: 10px;
        &:hover {
            background: #4ba09b;
        }
        &[disabled] svg {
            fill: #00000042;
        }
    }
`;

const TextApi = styled.div`
    font-family: "Montserrat";
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #181122;
    width: 100%;
    margin-bottom: 5px;
`;

const PlansGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 20px;
    width: 100%;
    max-width: 1008px;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: fadeIn 0.2s ease-in-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const ModalContainer = styled.div`
    display: flex;
    width: 600px;
    padding: 40px;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    border-radius: 8px;
    background: var(--Light-Pure, #fff);
    animation: slideUp 0.3s ease-in-out;

    @keyframes slideUp {
        from {
            transform: translateY(20px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }
`;

const PlanSummaryBox = styled.div`
    display: flex;
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    align-self: stretch;
    border-radius: 8px;
    border: 2px solid #463694;
    background: #f4f2fb;
`;

const PlanTitle = styled.div`
    color: var(--Secundary-Pure, #181122);
    font-family: Montserrat;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const PlanSubtitle = styled.div`
    color: var(--Secundary-Pure, #181122);
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const ActiveUsersText = styled.div`
    color: #181122;
    font-family: Montserrat;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    margin-top: 8px;
`;

const DividerLine = styled.div`
    height: 1px;
    align-self: stretch;
    background: #d9d9d9;
    margin: 8px 0px;
    width: 100%;
`;

const BillingInfoText = styled.div`
    color: #909090;
    font-family: Montserrat;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
`;

const TermsLabel = styled.span`
    color: #000;
    font-family: Montserrat;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const TermsLink = styled.a`
    color: #000;
    font-family: Montserrat;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
`;

const CancelWarningText = styled.div`
    color: #181122;
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 1.5;
    padding: 16px;
    background: #dc26261a;
    border-radius: 4px;
    border: 1px solid #DC2626;
`;

const ModalButtonsRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 16px;
`;

const SecondaryButton = styled(Button)`
    && {
        background: #fff;
        border: 1px solid #57c4c2;
        color: #57c4c2;
        font-family: Montserrat;
        font-size: 14px;
        font-weight: 600;
        text-transform: none;
        padding: 12px 24px;
        border-radius: 4px;

        &:hover {
            background: #f0f9f9;
        }
    }
`;

const LinkButton = styled(Button)`
    && {
        color: #419392;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        padding: 0px;
        line-height: 20px;
    }
`;

const PrimaryButton = styled(Button)`
    && {
        background: #57c4c2;
        color: #000;
        font-family: Montserrat;
        font-size: 14px;
        font-weight: 600;
        text-transform: none;
        padding: 12px 24px;
        border-radius: 4px;

        &:hover {
            background: #4ba09b;
        }

        &:disabled {
            background: #e4e4e4;
            color: #999;
        }
    }
`;

const CancelButton = styled(Button)`
    && {
        background: #DC2626;
        color: #fff;
        font-family: Montserrat;
        font-size: 14px;
        font-weight: 600;
        text-transform: none;
        padding: 12px 24px;
        border-radius: 4px;
        width: 100%;

        &:hover {
            background: #c54546;
        }

        &:disabled {
            background: #e4e4e4;
            color: #999;
        }
    }
`;

const ToastContainer = styled.div`
    position: fixed;
    top: 60px;
    right: 24px;
    display: flex;
    width: 356px;
    padding: 16px;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    border: 1px solid #e5e5e5;
    background: #fff;
    box-shadow: 0 4px 12px -1px rgba(0, 0, 0, 0.1);
    z-index: 10000;
    animation: slideInRight 0.3s ease-in-out;

    @keyframes slideInRight {
        from {
            transform: translateX(400px);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
`;

const ToastMessage = styled.div`
    color: #181122;
    font-family: Montserrat;
    font-size: 14px;
    font-weight: 600;
    line-height: normal;
    flex: 1;
`;

const ButtonBoxPlans = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    max-width: 1008px;
    margin-top: 32px;
`;

const TextButtonPlans = styled.span`
    color: #419392;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
`;

const ModalClose = styled.img`
    position: absolute;
    right: 0px;
    top: 0px;
    cursor: pointer;
    z-index: 10;
    width: 24px;
    height: 24px;
`;

const HeaderModalPlans = styled.div`
    width: 100%;
    position: relative;
`;

export default function Settings({ refreshProfile }) {
    let api = Api();
    let storage = Storage();
    let helper = Helper();
    const { impersonateCompanyId } = useImpersonation();
    const location = useLocation();

    const [profile, setProfile] = useState();
    const [token, setToken] = useState(null);
    const [projects, setProjects] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isOpen1, setIsOpen1] = useState(false);
    const [isOpen2, setIsOpen2] = useState(false);
    const [isOpen3, setIsOpen3] = useState(false);
    const [isOpen4, setIsOpen4] = useState(false);
    const [isOpen5, setIsOpen5] = useState(false);
    const [isOpen6, setIsOpen6] = useState(false);
    const [isOpen7, setIsOpen7] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(false);
    const [searchCC, setSearchCC] = useState("");
    const [searchCCStatus, setSearchCCStatus] = useState("todos");
    const [searchCategories, setSearchCategory] = useState("");
    const [searchCategoryStatus, setSearchCategoryStatus] = useState("todos");
    const [searchLoading, setSearchLoading] = useState(false);

    const [openPlanModal, setOpenPlanModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [companyCnpj, setCompanyCnpj] = useState("");
    const [acceptTermsOfUse, setAcceptTermsOfUse] = useState(false);
    const [planCnpj, setPlanCnpj] = useState("");
    const [planCnpjTouched, setPlanCnpjTouched] = useState(false);
    const [planCnpjError, setPlanCnpjError] = useState("");
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("success"); // 'success' or 'error'
    const [isCancelMode, setIsCancelMode] = useState(false);
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [mileageCost, setMileageCost] = useState();
    const [mileageRefundsEnabled, setMileageRefundsEnabled] = useState("");
    const [allowsCorporateCard, setAllowsCorporateCard] = useState("");
    const [company, setCompany] = useState("");
    const [enablePayments, setEnablePayments] = useState(false);
    const [recurringPlanId, setRecurringPlanId] = useState(null);
    const [activeUsersCount, setActiveUsersCount] = useState(0);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [hasPendingPayment, setHasPendingPayment] = useState(false);
    const [pendingPaymentUrl, setPendingPaymentUrl] = useState(null);
    const [subscriptionNextDueDate, setSubscriptionNextDueDate] =
        useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [buttonEnabled, setButtonEnabled] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openAddCategory, setOpenAddCategory] = useState(false);
    const [openAddProject, setOpenAddProject] = useState(false);
    const [selectChanged, setSelectChanged] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState("");
    const [showPlans, setShowPlans] = useState(false);

    function showErrorModal(message) {
        setErrorModalMessage(message);
        setErrorModalOpen(true);
    }

    function showToastMessage(message, type = "success") {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
        setTimeout(() => {
            setShowToast(false);
        }, 5000);
    }

    // Verificar se deve abrir automaticamente a seção de planos
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get("openPlans") === "true" && enablePayments) {
            closeAllDropdowns();
            setIsOpen7(true);

            const planId = searchParams.get("planId");
            if (planId && availablePlans.length > 0) {
                const plan = availablePlans.find(p => p.id === planId);
                if (plan) {
                    setSelectedPlan(plan);
                    setAcceptedTerms(false);
                    setPlanCnpj("");
                    setPlanCnpjTouched(false);
                    setPlanCnpjError("");
                    setIsCancelMode(false);
                    setOpenPlanModal(true);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, enablePayments, availablePlans]);

    // Atualizar acceptedTerms quando acceptTermsOfUse mudar
    useEffect(() => {
        setAcceptedTerms(acceptTermsOfUse);
    }, [acceptTermsOfUse]);

    function getTranslatedType(type) {
        if (type === "projects") return "centro de custo";
        if (type === "categories") return "categoria";
        return type;
    }

    const normalizeCnpj = (value) =>
        (value || "").toString().replace(/\D/g, "");

    const formatCnpj = (value) => {
        const digits = normalizeCnpj(value).slice(0, 14);

        const p1 = digits.slice(0, 2);
        const p2 = digits.slice(2, 5);
        const p3 = digits.slice(5, 8);
        const p4 = digits.slice(8, 12);
        const p5 = digits.slice(12, 14);

        let out = p1;
        if (digits.length > 2) out += `.${p2}`;
        if (digits.length > 5) out += `.${p3}`;
        if (digits.length > 8) out += `/${p4}`;
        if (digits.length > 12) out += `-${p5}`;
        return out;
    };

    const handleCnpjKeyDown = (e) => {
        if (e.ctrlKey || e.metaKey) return;

        const allowedKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
            "Home",
            "End",
        ];

        if (allowedKeys.includes(e.key)) return;
        if (!/^\d$/.test(e.key)) e.preventDefault();
    };

    const isValidCnpj = (value) => {
        const cnpj = normalizeCnpj(value);
        if (cnpj.length !== 14) return false;
        if (/^(\d)\1{13}$/.test(cnpj)) return false;

        const calcDigit = (base, weights) => {
            const sum = base
                .split("")
                .reduce(
                    (acc, digit, idx) =>
                        acc + parseInt(digit, 10) * weights[idx],
                    0
                );
            const remainder = sum % 11;
            return remainder < 2 ? "0" : String(11 - remainder);
        };

        const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const d1 = calcDigit(cnpj.slice(0, 12), weights1);
        const d2 = calcDigit(cnpj.slice(0, 12) + d1, weights2);
        return cnpj.slice(-2) === d1 + d2;
    };

    const getCnpjError = (value) => {
        const digits = normalizeCnpj(value);
        if (!digits) return "CNPJ é obrigatório";
        if (!isValidCnpj(digits)) return "CNPJ inválido";
        return "";
    };

    const getProfile = async () => {
        const data = await storage.getItem("data");
        if (!data) return;
        setToken(data.token);
        let response = await api.getProfile(data.token, impersonateCompanyId);
        setProfile(response.data.profile);
        setAcceptTermsOfUse(response?.data?.profile?.profile?.company?.acceptTermsOfUse || false);
    };

    const getPlans = async () => {
        const data = await storage.getItem("data");
        if (!data) return;
        try {
            let response = await api.getAvailablePlans(
                data.token,
                impersonateCompanyId
            );
            if (response.data?.availablePlans) {
                setAvailablePlans(response.data.availablePlans);
            }
        } catch (error) {
            console.error("Erro ao buscar planos:", error);
        }
    };

    const handleCancelSubscription = async () => {
        const data = await storage.getItem("data");
        if (!data) return;

        try {
            setLoading(true);
            const response = await api.cancelSubscription(
                data.token,
                impersonateCompanyId
            );

            if (response.data?.cancelSubscription?.success) {
                // Atualizar perfil para refletir mudanças
                await getProfile();

                // Fechar loading primeiro
                setLoading(false);

                // Fechar modal e resetar estado
                setOpenPlanModal(false);
                setIsCancelMode(false);

                // Aguardar um pequeno delay para garantir que o loading sumiu
                setTimeout(() => {
                    showToastMessage(
                        "Assinatura cancelada com sucesso!",
                        "success"
                    );
                }, 100);
            } else {
                setLoading(false);
                const errorMessage =
                    response.data?.cancelSubscription?.message ||
                    response.errors?.[0]?.message ||
                    "Erro ao cancelar assinatura. Tente novamente.";
                setTimeout(() => {
                    showToastMessage(errorMessage, "error");
                }, 100);
            }
        } catch (error) {
            console.error("Erro ao cancelar assinatura:", error);
            setLoading(false);
            setTimeout(() => {
                showToastMessage(
                    "Erro ao processar sua solicitação. Tente novamente.",
                    "error"
                );
            }, 100);
        }
    };

    const handleConfirmPlan = async () => {
        if (!selectedPlan || !acceptedTerms) return;

        const companyNeedsCnpj = normalizeCnpj(companyCnpj).length === 0;
        if (companyNeedsCnpj) {
            const error = getCnpjError(planCnpj);
            if (error) {
                setPlanCnpjTouched(true);
                setPlanCnpjError(error);
                return;
            }
        }

        const data = await storage.getItem("data");
        if (!data) return;

        try {
            setLoading(true);
            const response = await api.createSubscription(
                data.token,
                selectedPlan.id,
                impersonateCompanyId,
                normalizeCnpj(companyCnpj).length === 0
                    ? normalizeCnpj(planCnpj)
                    : null,
                selectedPlan.id !== "free" && acceptedTerms
            );

            if (response.data?.createSubscription?.success) {
                const { checkoutUrl, bankSlipUrl } =
                    response.data.createSubscription;
                const paymentUrl = checkoutUrl || bankSlipUrl;

                if (paymentUrl) {
                    window.open(paymentUrl, "_blank");
                }

                // Atualizar perfil para refletir mudanças
                await getProfile();
                refreshProfile();

                // Fechar loading primeiro
                setLoading(false);

                // Fechar modal e resetar estado
                setOpenPlanModal(false);
                setAcceptedTerms(true);

                // Mostrar mensagem de sucesso
                showToastMessage(
                    "Parabéns, seu novo plano foi configurado!",
                    "success"
                );
            } else {
                setLoading(false);
                const errorMessage =
                    response.data?.createSubscription?.message ||
                    response.errors?.[0]?.message ||
                    "Erro ao criar assinatura. Tente novamente.";

                if (/CNPJ/i.test(errorMessage)) {
                    setPlanCnpjTouched(true);
                    setPlanCnpjError(errorMessage);
                    return;
                }

                setTimeout(() => {
                    showToastMessage(errorMessage, "error");
                }, 100);
            }
        } catch (error) {
            console.error("Erro ao criar assinatura:", error);
            setLoading(false);
            setTimeout(() => {
                showToastMessage(
                    "Erro ao processar sua solicitação. Tente novamente.",
                    "error"
                );
            }, 100);
        }
    };

    const getProjects = async () => {
        const data = await storage.getItem("data");
        if (!data) return;
        try {
            let response = await api.getProjects(
                data.token,
                impersonateCompanyId
            );
            setProjects(response.data.allProjects.edges);
            setSearchLoading(false);
        } catch (error) {
            setSearchLoading(false);
            showErrorModal(
                "Não foi possível encontrar o centro de custo. Tente novamente mais tarde."
            );
        }
    };

    const getCategories = async () => {
        const data = await storage.getItem("data");
        if (!data) return;
        try {
            let response = await api.getCategories(
                data.token,
                impersonateCompanyId
            );
            setCategories(response.data.allCategories.edges);
            setSearchLoading(false);
        } catch (error) {
            setSearchLoading(false);
            showErrorModal(
                "Não foi possível encontrar a categoria. Tente novamente mais tarde."
            );
        }
    };

    const handleSearchProjects = async () => {
        try {
            searchProjectsWithStatus(searchCCStatus);
        } catch (error) {
            showErrorModal(
                "Não foi possível encontrar o centro de custo. Tente novamente mais tarde."
            );
        }
    };

    const handleSearchCategories = async () => {
        try {
            searchCategoriesWithStatus(searchCategoryStatus);
        } catch (error) {
            showErrorModal(
                "Não foi possível encontrar a categoria. Tente novamente mais tarde."
            );
        }
    };

    const searchProjectsWithStatus = (status) => {
        searchStatus("projects", searchCC, status);
    };

    const searchCategoriesWithStatus = (status) => {
        searchStatus("categories", searchCategories, status);
    };

    const searchStatus = async (type, searchTerm, status) => {
        setSearchLoading(true);

        const itemSetter = type === "projects" ? setProjects : setCategories;
        itemSetter([]);

        const data = await storage.getItem("data");
        if (!data) return;
        try {
            const fetchType =
                type === "projects" ? api.getProjects : api.getCategories;
            const response = await fetchType(data.token, impersonateCompanyId);

            const allItems =
                type === "projects"
                    ? response.data.allProjects.edges
                    : response.data.allCategories.edges;

            let filteredItems = allItems;
            if (searchTerm && searchTerm.trim() !== "") {
                filteredItems = allItems.filter((item) =>
                    item.node.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                );
            }

            if (status === "ativo") {
                filteredItems = filteredItems.filter(
                    (item) => !item.node.disabled
                );
            }
            if (status === "inativo") {
                filteredItems = filteredItems.filter(
                    (item) => item.node.disabled
                );
            }

            itemSetter(filteredItems);
            setSearchLoading(false);
        } catch (error) {
            console.error(`Erro ao buscar ${type}:`, error);
            showErrorModal(
                `Não foi possível encontrar ${getTranslatedType(
                    type
                )}. Tente novamente mais tarde.`
            );

            setSearchLoading(false);
        }
    };

    const handleClear = (type) => {
        if (type === "project") {
            // setProjects([]);
            setSearchCC("");
            // setSearchCCStatus('todos');
            getProjects();
        }
        if (type === "category") {
            // setCategories([]);
            setSearchCategory("");
            // setSearchCategoryStatus('todos');
            getCategories();
        }
    };

    const closeAllDropdowns = () => {
        setIsOpen1(false);
        setIsOpen2(false);
        setIsOpen3(false);
        setIsOpen4(false);
        setIsOpen5(false);
        setIsOpen6(false);
        setIsOpen7(false);
    };

    const toggleDropdown1 = () => {
        closeAllDropdowns();
        setIsOpen1(!isOpen1);
    };

    const toggleDropdown2 = () => {
        closeAllDropdowns();
        setIsOpen2(!isOpen2);
    };

    const toggleDropdown3 = () => {
        closeAllDropdowns();
        setIsOpen3(!isOpen3);
    };

    const toggleDropdown4 = () => {
        closeAllDropdowns();
        setIsOpen4(!isOpen4);
    };

    const toggleDropdown5 = () => {
        closeAllDropdowns();
        setIsOpen5(!isOpen5);
    };

    const toggleDropdown6 = () => {
        closeAllDropdowns();
        setIsOpen6(!isOpen6);
    };

    const toggleDropdown7 = () => {
        closeAllDropdowns();
        setIsOpen7(!isOpen7);
    };

    const handleCloseModal = () => {
        setSelectedProject(null);
        setselectedCategory(null);
        setOpenEdit(false);
        setOpenAddCategory(false);
        setOpenAddProject(false);

        const lastCCStatus =
            localStorage.getItem("lastCCStatus") || searchCCStatus;
        const lastCategoryStatus =
            localStorage.getItem("lastCategoryStatus") || searchCategoryStatus;
        searchProjectsWithStatus(lastCCStatus);
        searchCategoriesWithStatus(lastCategoryStatus);
    };

    const handleOpenEdit = () => {
        setOpenEdit(true);
        setOpenAddCategory(false);
        setOpenAddProject(false);
    };

    useEffect(() => {
        if (!openEdit && !openAddCategory && !openAddProject) {
            getProfile();
            getPlans();
            searchProjectsWithStatus(searchCCStatus);
            searchCategoriesWithStatus(searchCategoryStatus);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openEdit, openAddCategory, openAddProject]);

    const handleCCStatusChange = (event) => {
        const newStatus = event.target.value;
        setSearchCCStatus(newStatus);
        searchProjectsWithStatus(newStatus);
    };

    const handleCategoryStatusChange = (event) => {
        const newStatus = event.target.value;
        setSearchCategoryStatus(newStatus);
        searchCategoriesWithStatus(newStatus);
    };

    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedCategory, setselectedCategory] = useState(null);

    useEffect(() => {
        if (profile) {
            setName(profile?.profile?.company?.name);
            setPhone(profile?.profile?.company?.phone);
            setCompanyCnpj(profile?.profile?.company?.cnpj || "");
            setCompany(
                parseInt(atob(profile?.profile?.company?.id).split(":")[1])
            );
            setMileageCost(
                profile?.profile?.company?.mileageCost
                    .toFixed(2)
                    .replace(".", ",")
            );
            setMileageRefundsEnabled(
                profile?.profile?.company?.acceptMileage ? "True" : "False"
            );
            setAllowsCorporateCard(
                profile?.profile?.company?.allowCorporate ? "True" : "False"
            );
            setEnablePayments(
                profile?.profile?.company?.enablePayments || false
            );
            setRecurringPlanId(
                profile?.profile?.company?.recurringPlanId || null
            );
            setActiveUsersCount(
                profile?.profile?.company?.activeUsersCount || 0
            );
            setHasActiveSubscription(
                profile?.profile?.company?.hasActiveSubscription || false
            );
            setHasPendingPayment(
                profile?.profile?.company?.hasPendingPayment || false
            );
            setPendingPaymentUrl(
                profile?.profile?.company?.pendingPaymentUrl || null
            );
            setSubscriptionNextDueDate(
                profile?.profile?.company?.subscriptionNextDueDate || null
            );
        }
    }, [profile]);

    useEffect(() => {
        if (enablePayments && availablePlans.length === 0) {
            getPlans();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enablePayments]);

    // Refresh automático quando há pagamento pendente
    useEffect(() => {
        let intervalId;

        if (hasPendingPayment) {
            // Atualizar a cada 5 segundos
            intervalId = setInterval(() => {
                getProfile();
            }, 5000);
        }

        // Limpar intervalo quando componente desmontar ou quando não houver mais pagamento pendente
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPendingPayment]);

    const [impersonatedCompany, setImpersonatedCompany] = useState({});

    useEffect(() => {
        if (impersonatedCompany) {
            setPhone(impersonatedCompany.phone);
            setName(impersonatedCompany.name);
            const rawMileageCost = impersonatedCompany.mileageCost;
            const formattedMileageCost = !isNaN(parseFloat(rawMileageCost))
                ? parseFloat(rawMileageCost).toFixed(2).replace(".", ",")
                : "0,00";
            setMileageCost(formattedMileageCost);
            setMileageRefundsEnabled(
                impersonatedCompany.mileageRefundsEnabled ? "True" : "False"
            );
            setAllowsCorporateCard(
                impersonatedCompany.allowsCorporateCard ? "True" : "False"
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [impersonatedCompany]);

    useEffect(() => {
        if (
            name !== profile?.profile?.company?.name ||
            phone !== profile?.profile?.company?.phone ||
            mileageCost !== profile?.profile?.company?.mileageCost
        ) {
            setButtonEnabled(true);
        } else {
            setButtonEnabled(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, phone, mileageCost]);

    const editCompany = async () => {
        setLoading(true);
        try {
            let formattedMileage = parseFloat(
                mileageCost.replace("R$ ", "").replace(",", ".")
            );
            await api.editCompany(
                token,
                name,
                phone,
                allowsCorporateCard,
                mileageRefundsEnabled,
                formattedMileage,
                company,
                impersonateCompanyId
            );
            if (refreshProfile) {
                refreshProfile();
            }
        } catch (error) {
            showErrorModal(
                "Não foi possível salvar os dados da empresa. Tente novamente mais tarde."
            );
        }
        setLoading(false);
    };
    const saveMileageCost = async () => {
        setLoading(true);
        try {
            let formattedMileage = parseFloat(
                mileageCost.replace("R$ ", "").replace(",", ".")
            );
            await api.editCompany(
                token,
                name,
                phone,
                allowsCorporateCard,
                mileageRefundsEnabled,
                formattedMileage,
                company,
                impersonateCompanyId
            );
            if (refreshProfile) {
                refreshProfile();
            }
        } catch (error) {
            showErrorModal(
                "Não foi possível salvar quilometragem. Tente novamente mais tarde."
            );
        }
        setLoading(false);
    };

    const exportCategoriesToCSV = async () => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;

            const params = new URLSearchParams();

            let statusValue;

            if (searchCategoryStatus === "inativo") {
                statusValue = "inactive";
            } else if (searchCategoryStatus === false) {
                statusValue = "ativo";
            } else {
                statusValue = "all";
            }

            params.append("status", statusValue);
            if (searchCategories !== null)
                params.append("name", searchCategories);

            const url = `${API_URL}/reports/categories/csv/?${params}`;

            const data = await storage.getItem("data");

            const options = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: data.token,
                },
            };

            if (impersonateCompanyId) {
                options.headers.Impersonate = impersonateCompanyId;
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                if (response.status === 400) {
                    const errorMessage = await response.text();
                    throw new Error(errorMessage);
                }

                throw new Error(
                    "Erro ao gerar CSV. \nTente novamente mais tarde."
                );
            }

            const blob = await response.blob();
            const fileURL = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = fileURL;
            a.download = "zag_categorias.csv";
            a.click();
            window.URL.revokeObjectURL(fileURL);
        } catch (error) {
            showErrorModal(
                error.message ||
                    "Não foi possível gerar o CSV. \nTente novamente mais tarde."
            );
        }
    };

    const exportCategoriesToPDF = async () => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;

            const params = new URLSearchParams();

            let statusValue;

            if (searchCategoryStatus === "inativo") {
                statusValue = "inactive";
            } else if (searchCategoryStatus === false) {
                statusValue = "ativo";
            } else {
                statusValue = "all";
            }

            params.append("status", statusValue);
            if (searchCategories !== null)
                params.append("name", searchCategories);

            const url = `${API_URL}/reports/categories/pdf/?${params}`;

            const data = await storage.getItem("data");

            const options = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: data.token,
                },
            };

            if (impersonateCompanyId) {
                options.headers.Impersonate = impersonateCompanyId;
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                if (response.status === 400) {
                    const errorMessage = await response.text();
                    throw new Error(errorMessage);
                }

                throw new Error(
                    "Erro ao gerar PDF. \nTente novamente mais tarde."
                );
            }

            const blob = await response.blob();
            const fileURL = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = fileURL;
            a.download = "zag_categorias.pdf";
            a.click();
            window.URL.revokeObjectURL(fileURL);
        } catch (error) {
            showErrorModal(
                error.message ||
                    "Não foi possível gerar o PDF. \nTente novamente mais tarde."
            );
        }
    };

    const exportCCToCSV = async () => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;

            const params = new URLSearchParams();

            let statusValue;

            if (searchCCStatus === "inativo") {
                statusValue = "inactive";
            } else if (searchCCStatus === false) {
                statusValue = "ativo";
            } else {
                statusValue = "all";
            }

            params.append("status", statusValue);
            if (searchCC !== null) params.append("name", searchCC);

            const url = `${API_URL}/reports/projects/csv/?${params}`;

            const data = await storage.getItem("data");

            const options = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: data.token,
                },
            };

            if (impersonateCompanyId) {
                options.headers.Impersonate = impersonateCompanyId;
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                if (response.status === 400) {
                    const errorMessage = await response.text();
                    throw new Error(errorMessage);
                }

                throw new Error(
                    "Erro ao gerar CSV. \nTente novamente mais tarde."
                );
            }

            const blob = await response.blob();
            const fileURL = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = fileURL;
            a.download = "zag_centros_de_custo.csv";
            a.click();
            window.URL.revokeObjectURL(fileURL);
        } catch (error) {
            showErrorModal(
                error.message ||
                    "Não foi possível gerar o CSV. \nTente novamente mais tarde."
            );
        }
    };

    const exportCCToPDF = async () => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;

            const params = new URLSearchParams();

            let statusValue;

            if (searchCCStatus === "inativo") {
                statusValue = "inactive";
            } else if (searchCCStatus === false) {
                statusValue = "ativo";
            } else {
                statusValue = "all";
            }

            params.append("status", statusValue);
            if (searchCC !== null) params.append("name", searchCC);

            const url = `${API_URL}/reports/projects/pdf/?${params}`;
            const data = await storage.getItem("data");

            const options = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: data.token,
                },
            };

            if (impersonateCompanyId) {
                options.headers.Impersonate = impersonateCompanyId;
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                if (response.status === 400) {
                    const errorMessage = await response.text();
                    throw new Error(errorMessage);
                }

                throw new Error(
                    "Erro ao gerar PDF. \nTente novamente mais tarde."
                );
            }

            const blob = await response.blob();
            const fileURL = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = fileURL;
            a.download = "zag_centros_de_custo.pdf";
            a.click();
            window.URL.revokeObjectURL(fileURL);
        } catch (error) {
            showErrorModal(
                error.message ||
                    "Não foi possível gerar o PDF. \nTente novamente mais tarde."
            );
        }
    };

    function CustomExportCategoriesMenu() {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);
        const handleClick = (event) => setAnchorEl(event.currentTarget);
        const handleClose = () => setAnchorEl(null);

        return (
            <GridToolbarContainer>
                <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleClick}
                    size="small"
                >
                    Exportar
                </Button>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem
                        onClick={() => {
                            exportCategoriesToCSV();
                            handleClose();
                        }}
                    >
                        <DownloadIcon
                            fontSize="small"
                            style={{ marginRight: 8 }}
                        />
                        Exportar como CSV
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            exportCategoriesToPDF();
                            handleClose();
                        }}
                    >
                        <DownloadIcon
                            fontSize="small"
                            style={{ marginRight: 8 }}
                        />
                        Exportar como PDF
                    </MenuItem>
                </Menu>
            </GridToolbarContainer>
        );
    }

    function CustomExportCCMenu() {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);
        const handleClick = (event) => setAnchorEl(event.currentTarget);
        const handleClose = () => setAnchorEl(null);

        return (
            <GridToolbarContainer>
                <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleClick}
                    size="small"
                >
                    Exportar
                </Button>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem
                        onClick={() => {
                            exportCCToCSV();
                            handleClose();
                        }}
                    >
                        <DownloadIcon
                            fontSize="small"
                            style={{ marginRight: 8 }}
                        />
                        Exportar como CSV
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            exportCCToPDF();
                            handleClose();
                        }}
                    >
                        <DownloadIcon
                            fontSize="small"
                            style={{ marginRight: 8 }}
                        />
                        Exportar como PDF
                    </MenuItem>
                </Menu>
            </GridToolbarContainer>
        );
    }

    useEffect(() => {
        if (selectChanged) {
            saveMileageCost(
                name,
                phone,
                allowsCorporateCard,
                mileageRefundsEnabled,
                mileageCost,
                company
            );
            setSelectChanged(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowsCorporateCard, mileageRefundsEnabled, selectChanged]);

    useEffect(() => {
        if (selectChanged) {
            editCompany(
                name,
                phone,
                allowsCorporateCard,
                mileageRefundsEnabled,
                mileageCost,
                company
            );
            setSelectChanged(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowsCorporateCard, mileageRefundsEnabled, selectChanged]);

    const defaultMaskOptions = {
        prefix: "R$ ",
        suffix: "",
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: "",
        allowDecimal: true,
        decimalSymbol: ",",
        decimalLimit: 2,
        integerLimit: 7,
        allowNegative: false,
        allowLeadingZeroes: false,
    };

    const currencyMask = createNumberMask({
        ...defaultMaskOptions,
    });

    const fetchCompanyData = async () => {
        if (!impersonateCompanyId) return;

        const data = await storage.getItem("data");
        const response = await api.getImpersonatedCompany(
            data.token,
            impersonateCompanyId
        );

        if (response.data.allCompanies.edges) {
            setImpersonatedCompany(response.data.allCompanies.edges[0].node);
        }
    };

    useEffect(() => {
        fetchCompanyData();
        if (impersonateCompanyId === null) {
            getProfile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [impersonateCompanyId]);

    const decodeId = (encodedId) => {
        const decoded = atob(encodedId);
        const parts = decoded.split(":");
        return parts[1];
    };

    useEffect(() => {
        if (
            name !== profile?.profile?.company?.name ||
            phone !== profile?.profile?.company?.phone
        ) {
            setButtonEnabled(true);
        } else {
            setButtonEnabled(false);
        }
    }, [name, phone, profile]);

    const getFilteredPlans = () => {
        if (showPlans) {
            return availablePlans;
        }

        const currentPlanId = recurringPlanId;
        const width = window.innerWidth;

        if (width >= 1130) {
            const plansToCheck = ['free', 'zag1', 'zag2', 'zag3', 'zag4', 'zag5', 'zag6', 'zag7'];
            if (plansToCheck.includes(currentPlanId)) {
                return availablePlans.slice(0, 8);
            }
            return availablePlans.slice(8);
        }

        if (width >= 874) {
            const firstGroup = ['free', 'zag1', 'zag2', 'zag3', 'zag4', 'zag5'];
            const secondGroup = ['zag6', 'zag7', 'zag8'];

            if (firstGroup.includes(currentPlanId)) {
                return availablePlans.slice(0, 6);
            }
            if (secondGroup.includes(currentPlanId)) {
                return availablePlans.slice(3, 9);
            }
            return availablePlans.slice(9);
        }

        if (width >= 620) {
            const firstGroup = ['free', 'zag1', 'zag2', 'zag3'];
            const secondGroup = ['zag4', 'zag5', 'zag6', 'zag7'];
            const thirdGroup = ['zag8', 'zag9', 'zag10', 'zag11'];

            if (firstGroup.includes(currentPlanId)) {
                return availablePlans.slice(0, 4);
            }
            if (secondGroup.includes(currentPlanId)) {
                return availablePlans.slice(4, 8);
            }
            if (thirdGroup.includes(currentPlanId)) {
                return availablePlans.slice(8, 12);
            }
            return availablePlans.slice(-4);
        }

        const groups = [
            { ids: ['free', 'zag1'], slice: [0, 2] },
            { ids: ['zag2', 'zag3'], slice: [2, 4] },
            { ids: ['zag4', 'zag5'], slice: [4, 6] },
            { ids: ['zag6', 'zag7'], slice: [6, 8] },
            { ids: ['zag8', 'zag9'], slice: [8, 10] },
            { ids: ['zag10', 'zag11'], slice: [10, 12] },
            { ids: ['zag12', 'zag13'], slice: [12, 14] },
        ];

        for (const group of groups) {
            if (group.ids.includes(currentPlanId)) {
                return availablePlans.slice(group.slice[0], group.slice[1]);
            }
        }

        return availablePlans.slice(0, 2);
    };

    return (
        <>
            {loading && <Loader />}
            <Edit
                type={"edit"}
                open={openEdit}
                close={handleCloseModal}
                project={selectedProject}
                categorie={selectedCategory}
                impersonate={impersonateCompanyId}
            />
            <AddCategory
                type={"add"}
                open={openAddCategory}
                close={handleCloseModal}
                profile={selectedProfile}
                impersonate={impersonateCompanyId}
            />
            <AddProject
                type={"add"}
                open={openAddProject}
                close={handleCloseModal}
                profile={selectedProfile}
                impersonate={impersonateCompanyId}
            />
            <Holder>
                <Content title={"Configurações"}>
                    <Grid
                        container
                        direction="column"
                        justifyContent="space-evenly"
                        alignItems="stretch"
                    >
                        <Button
                            onClick={toggleDropdown1}
                            variant="outlined"
                            style={{
                                border: "none",
                                marginTop: 15,
                                padding: 0,
                            }}
                        >
                            {isOpen1 ? (
                                <ImageIcon src="/img/arrow_up.svg" alt="" />
                            ) : (
                                <ImageIcon src="/img/arrow_down.svg" alt="" />
                            )}
                            DADOS DA EMPRESA
                        </Button>
                        <Box
                            style={{
                                paddingBottom: 20,
                                border: "none",
                                borderBottom: "1px solid #E6E6E6",
                                width: "100%",
                                paddingTop: 10,
                                paddingLeft: 10,
                            }}
                        >
                            <Collapse in={isOpen1}>
                                <Field>
                                    <FieldContainer>
                                        <SubTitle>EMPRESA</SubTitle>
                                        <TextField
                                            id="outlined-basic"
                                            value={name}
                                            variant="outlined"
                                            style={{ borderRadius: 5 }}
                                            onChange={(event) => {
                                                if (!impersonateCompanyId) {
                                                    setName(event.target.value);
                                                }
                                            }}
                                            disabled={!!impersonateCompanyId}
                                        />
                                    </FieldContainer>
                                    <FieldContainer>
                                        <SubTitle>TELEFONE</SubTitle>
                                        <TextField
                                            id="outlined-basic"
                                            value={helper.formatPhoneNumber(
                                                phone
                                            )}
                                            variant="outlined"
                                            style={{ borderRadius: 5 }}
                                            onChange={(event) => {
                                                if (!impersonateCompanyId) {
                                                    setPhone(
                                                        helper.formatPhoneNumber(
                                                            event.target.value
                                                        )
                                                    );
                                                }
                                            }}
                                            disabled={!!impersonateCompanyId}
                                        />
                                    </FieldContainer>
                                    <CenteredFieldContainer>
                                        <Button
                                            style={{
                                                fontSize: 12,
                                                opacity: 1,
                                                marginTop: 5,
                                                height: 50,
                                                padding: 20,
                                                // eslint-disable-next-line no-dupe-keys
                                                opacity: buttonEnabled
                                                    ? 1
                                                    : 0.4,
                                            }}
                                            variant={"outlined"}
                                            color={
                                                buttonEnabled
                                                    ? "primary"
                                                    : "secondary"
                                            }
                                            onClick={() =>
                                                editCompany(name, phone)
                                            }
                                            disabled={
                                                !!impersonateCompanyId ||
                                                !buttonEnabled
                                            }
                                        >
                                            SALVAR
                                        </Button>
                                    </CenteredFieldContainer>
                                </Field>
                            </Collapse>
                        </Box>
                        {enablePayments && (
                            <>
                                <Button
                                    onClick={toggleDropdown7}
                                    variant="outlined"
                                    style={{
                                        border: "none",
                                        marginTop: 15,
                                        padding: 0,
                                    }}
                                >
                                    {isOpen7 ? (
                                        <ImageIcon
                                            src="/img/arrow_up.svg"
                                            alt=""
                                        />
                                    ) : (
                                        <ImageIcon
                                            src="/img/arrow_down.svg"
                                            alt=""
                                        />
                                    )}
                                    PLANOS E ASSINATURAS
                                </Button>
                                <Box
                                    style={{
                                        paddingBottom: 20,
                                        border: "none",
                                        borderBottom: "1px solid #E6E6E6",
                                        width: "100%",
                                        paddingTop: 10,
                                        paddingLeft: 10,
                                    }}
                                >
                                    <Collapse in={isOpen7}>
                                        <Field
                                            style={{
                                                width: "100%",
                                                flexDirection: "column",
                                            }}
                                        >
                                            {/* Plano Legado: free com mais usuários que o permitido */}
                                            {recurringPlanId === "free" &&
                                                availablePlans.find(
                                                    (p) => p.id === "free"
                                                )?.maxUsers &&
                                                activeUsersCount >
                                                    availablePlans.find(
                                                        (p) => p.id === "free"
                                                    ).maxUsers && (
                                                    <CurrentSubscription
                                                        isPaidPlan={false}
                                                        planTitle={<strong>Plano Gratuito 2025 (legado).</strong>}
                                                        activeUsersCount={activeUsersCount}
                                                        statusBadgeProps={{
                                                            pending: false,
                                                            active: false,
                                                            statusText: "Status: Descontinuado"
                                                        }}
                                                        tooltipContent={
                                                            <TooltipContent>
                                                                À partir de 01 de janeiro de 2026 seu plano atual será descontinuado.
                                                                Atualize seu plano e evite um possível bloqueio dos usuários.
                                                            </TooltipContent>
                                                        }
                                                    />
                                                )}
                                            {/* Plano atual (não-free) */}
                                            {recurringPlanId &&
                                                recurringPlanId !== "free" && (
                                                    <CurrentSubscription
                                                        isPaidPlan={true}
                                                        planTitle={
                                                            availablePlans.find(
                                                                (p) => p.id === recurringPlanId
                                                            )?.description || ""
                                                        }
                                                        activeUsersCount={activeUsersCount}
                                                        statusBadgeProps={{
                                                            pending: hasPendingPayment,
                                                            active: hasActiveSubscription,
                                                            statusText: `Status: ${
                                                                hasPendingPayment
                                                                    ? "Pendente"
                                                                    : hasActiveSubscription
                                                                    ? "Ativo"
                                                                    : "Cancelado"
                                                            }`
                                                        }}
                                                        tooltipContent={
                                                            hasPendingPayment ? (
                                                                <TooltipContent>
                                                                    Estamos aguardando retorno sobre seu pagamento.
                                                                    Fique atento, se em até 48h não for confirmado,
                                                                    seus usuários poderão ser bloqueados.{" "}
                                                                    {pendingPaymentUrl && (
                                                                        <PaymentLink
                                                                            href={pendingPaymentUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            Efetuar Pagamento
                                                                        </PaymentLink>
                                                                    )}
                                                                </TooltipContent>
                                                            ) : (
                                                                ""
                                                            )
                                                        }
                                                    />
                                                )}
                                            <BasicTitle title={"Planos"} />
                                            <PlansGrid $showPlans={showPlans}>
                                                {getFilteredPlans().map((plan) => {
                                                    const isCurrentPlan =
                                                        plan.id ===
                                                        recurringPlanId;
                                                    const cannotSelect =
                                                        activeUsersCount >
                                                        plan.maxUsers;

                                                    const isPendingPayment =
                                                        isCurrentPlan &&
                                                        hasPendingPayment;

                                                    const isDisabled =
                                                        (isCurrentPlan &&
                                                            hasActiveSubscription &&
                                                            !hasPendingPayment) ||
                                                        cannotSelect;

                                                    const tooltipText =
                                                        cannotSelect
                                                            ? `Sua empresa tem ${activeUsersCount} usuários ativos.`
                                                            : "";

                                                    let buttonText =
                                                        "Selecionar";
                                                    let buttonAction = () => {
                                                        setSelectedPlan(plan);
                                                        setPlanCnpj("");
                                                        setPlanCnpjTouched(
                                                            false
                                                        );
                                                        setPlanCnpjError("");
                                                        setIsCancelMode(false);
                                                        setOpenPlanModal(true);
                                                    };

                                                    if (isPendingPayment) {
                                                        buttonText =
                                                            "Efetuar Pagamento";
                                                        buttonAction = () => {
                                                            if (
                                                                pendingPaymentUrl
                                                            ) {
                                                                window.open(
                                                                    pendingPaymentUrl,
                                                                    "_blank"
                                                                );
                                                            }
                                                        };
                                                    } else if (
                                                        isCurrentPlan &&
                                                        hasActiveSubscription
                                                    ) {
                                                        buttonText =
                                                            "Assinatura Atual";
                                                        buttonAction = () => {
                                                            setSelectedPlan(
                                                                plan
                                                            );
                                                            setPlanCnpj("");
                                                            setPlanCnpjTouched(
                                                                false
                                                            );
                                                            setPlanCnpjError(
                                                                ""
                                                            );
                                                            setIsCancelMode(
                                                                true
                                                            );
                                                            setOpenPlanModal(
                                                                true
                                                            );
                                                        };
                                                    } else if (
                                                        recurringPlanId === "free" &&
                                                        plan.id === "free"
                                                    ) {
                                                        buttonText =
                                                            "Assinatura Atual";
                                                        buttonAction = () => {
                                                            setSelectedPlan(
                                                                plan
                                                            );
                                                            setPlanCnpj("");
                                                            setPlanCnpjTouched(
                                                                false
                                                            );
                                                            setPlanCnpjError(
                                                                ""
                                                            );
                                                            setIsCancelMode(
                                                                true
                                                            );
                                                            setOpenPlanModal(
                                                                true
                                                            );
                                                        };
                                                    }

                                                    const isFreePlan = (plan.id === "free" && recurringPlanId === "free") || hasActiveSubscription;

                                                    return (
                                                        <PlanCard
                                                            key={plan.id}
                                                            title={
                                                                plan.price === 0
                                                                    ? plan.name
                                                                    : `R$ ${plan.price
                                                                          .toFixed(
                                                                              2
                                                                          )
                                                                          .replace(
                                                                              ".",
                                                                              ","
                                                                          )}`
                                                            }
                                                            subtitle={
                                                                plan.description
                                                            }
                                                            buttonText={
                                                                buttonText
                                                            }
                                                            buttonTextOnHover={
                                                                "Cancelar Assinatura"
                                                            }
                                                            onButtonClick={
                                                                buttonAction
                                                            }
                                                            disabled={
                                                                isDisabled
                                                            }
                                                            isActive={
                                                                isCurrentPlan
                                                            }
                                                            tooltipText={
                                                                tooltipText
                                                            }
                                                            isPending={
                                                                isPendingPayment
                                                            }
                                                            canCancel={
                                                                isCurrentPlan &&
                                                                isFreePlan &&
                                                                !hasPendingPayment
                                                            }
                                                        />
                                                    );
                                                })}
                                            </PlansGrid>
                                            {(() => {
                                                const width = window.innerWidth;
                                                let minPlans = 8;

                                                if (width >= 1130) minPlans = 8;
                                                else if (width >= 874) minPlans = 6;
                                                else if (width >= 620) minPlans = 4;
                                                else minPlans = 2;

                                                return availablePlans.length > minPlans;
                                            })() && (
                                                <ButtonBoxPlans>
                                                    <SecondaryButton
                                                        onClick={() => {
                                                            setShowPlans(!showPlans);
                                                        }}
                                                        style={{ borderColor: '#419392', borderRadius: 8 }}
                                                    >
                                                        <TextButtonPlans>
                                                            {showPlans ? "Minimizar PLANOS" : "Visualizar TODOS OS PLANOS"}
                                                        </TextButtonPlans>
                                                    </SecondaryButton>
                                                </ButtonBoxPlans>
                                            )}
                                        </Field>
                                    </Collapse>
                                </Box>
                            </>
                        )}
                        <Button
                            onClick={toggleDropdown2}
                            variant="outlined"
                            style={{
                                border: "none",
                                marginTop: 15,
                                padding: 0,
                            }}
                        >
                            {isOpen2 ? (
                                <ImageIcon src="/img/arrow_up.svg" alt="" />
                            ) : (
                                <ImageIcon src="/img/arrow_down.svg" alt="" />
                            )}
                            CARTÃO CORPORATIVO
                        </Button>
                        <Box
                            style={{
                                paddingBottom: 20,
                                border: "none",
                                borderBottom: "1px solid #E6E6E6",
                                width: "100%",
                                paddingTop: 10,
                                paddingLeft: 10,
                            }}
                        >
                            <Collapse in={isOpen2}>
                                <Field>
                                    <FieldContainer>
                                        <SubTitle>ESCOLHER OPÇÃO</SubTitle>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            style={{
                                                width: 180,
                                            }}
                                            value={allowsCorporateCard}
                                            onChange={(event) => {
                                                setAllowsCorporateCard(
                                                    event.target.value
                                                );
                                                setSelectChanged(true);
                                            }}
                                            disabled={!!impersonateCompanyId}
                                        >
                                            <MenuItem key={1} value="True">
                                                ATIVO
                                            </MenuItem>
                                            <MenuItem key={2} value="False">
                                                INATIVO
                                            </MenuItem>
                                        </Select>
                                    </FieldContainer>
                                </Field>
                            </Collapse>
                        </Box>
                        <Button
                            onClick={toggleDropdown3}
                            variant="outlined"
                            style={{
                                border: "none",
                                marginTop: 15,
                                padding: 0,
                            }}
                        >
                            {isOpen3 ? (
                                <ImageIcon src="/img/arrow_up.svg" alt="" />
                            ) : (
                                <ImageIcon src="/img/arrow_down.svg" alt="" />
                            )}
                            QUILOMETRAGEM
                        </Button>
                        <Box
                            style={{
                                paddingBottom: 20,
                                border: "none",
                                borderBottom: "1px solid #E6E6E6",
                                width: "100%",
                                paddingTop: 10,
                                paddingLeft: 10,
                            }}
                        >
                            <Collapse in={isOpen3}>
                                <FieldContainer>
                                    <SubTitle style={{ marginTop: 20 }}>
                                        ESCOLHER OPÇÃO
                                    </SubTitle>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        style={{
                                            width: 180,
                                        }}
                                        value={mileageRefundsEnabled}
                                        onChange={(event) => {
                                            setMileageRefundsEnabled(
                                                event.target.value
                                            );
                                            setSelectChanged(true);
                                        }}
                                        disabled={!!impersonateCompanyId}
                                    >
                                        <MenuItem value="True">ATIVO</MenuItem>
                                        <MenuItem value="False">
                                            INATIVO
                                        </MenuItem>
                                    </Select>
                                </FieldContainer>
                                <Field style={{ marginTop: 20 }}>
                                    <FieldContainer>
                                        <SubTitle>VALOR POR KM</SubTitle>
                                        <MaskedInput
                                            mask={currencyMask}
                                            placeholder="R$ 0,00"
                                            style={{
                                                fontSize: 16,
                                                padding: 14,
                                                border: "1px solid #C1C4C8",
                                                borderRadius: 4,
                                                marginRight: 16,
                                                width: 200,
                                                fontWeight: 400,
                                                color:
                                                    impersonateCompanyId ===
                                                    null
                                                        ? "black"
                                                        : "rgba(0, 0, 0, 0.38)",
                                                backgroundColor:
                                                    "rgba(0, 0, 0, 0)",
                                            }}
                                            value={`R$ ${mileageCost}`}
                                            onChange={(event) => {
                                                setMileageCost(
                                                    event.target.value
                                                );
                                            }}
                                            disabled={!!impersonateCompanyId}
                                        />
                                    </FieldContainer>
                                    <CenteredFieldContainer>
                                        <Button
                                            style={{
                                                fontSize: 12,
                                                opacity: 1,
                                                marginTop: 5,
                                                height: 50,
                                                padding: 20,
                                                // eslint-disable-next-line no-dupe-keys
                                                opacity: buttonEnabled
                                                    ? 1
                                                    : 0.4,
                                            }}
                                            variant={"outlined"}
                                            color={
                                                buttonEnabled
                                                    ? "primary"
                                                    : "secondary"
                                            }
                                            onClick={() =>
                                                saveMileageCost(mileageCost)
                                            }
                                            disabled={!!impersonateCompanyId}
                                        >
                                            SALVAR
                                        </Button>
                                    </CenteredFieldContainer>
                                </Field>
                            </Collapse>
                        </Box>

                        <Button
                            onClick={toggleDropdown4}
                            variant="outlined"
                            style={{
                                border: "none",
                                marginTop: 15,
                                padding: 0,
                            }}
                        >
                            {isOpen4 ? (
                                <ImageIcon src="/img/arrow_up.svg" alt="" />
                            ) : (
                                <ImageIcon src="/img/arrow_down.svg" alt="" />
                            )}
                            CENTRO DE CUSTO
                        </Button>
                        <Box
                            style={{
                                paddingBottom: 20,
                                border: "none",
                                borderBottom: "1px solid #E6E6E6",
                                width: "100%",
                                paddingTop: 10,
                                paddingLeft: 10,
                            }}
                        >
                            <Collapse in={isOpen4}>
                                <View
                                    style={{
                                        paddingTop: "24px",
                                        paddingBottom: "40px",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: 24,
                                        }}
                                    >
                                        <FieldContainer>
                                            <SubTitle>Nome</SubTitle>
                                            <View>
                                                <TextField
                                                    value={searchCC}
                                                    variant="outlined"
                                                    style={{ borderRadius: 5 }}
                                                    onChange={(event) => {
                                                        setSearchCC(
                                                            event.target.value
                                                        );
                                                    }}
                                                    onKeyUp={(e) => {
                                                        if (e.key === "Enter")
                                                            handleSearchProjects();
                                                    }}
                                                    InputProps={{
                                                        endAdornment:
                                                            searchCC ? (
                                                                <IconButton
                                                                    style={{
                                                                        position:
                                                                            "absolute",
                                                                        right: 12,
                                                                    }}
                                                                    onClick={() =>
                                                                        handleClear(
                                                                            "project"
                                                                        )
                                                                    }
                                                                    edge="end"
                                                                >
                                                                    <ClearIcon />
                                                                </IconButton>
                                                            ) : null,
                                                    }}
                                                />
                                                <Button
                                                    style={{
                                                        marginLeft: 16,
                                                        fontSize: 12,
                                                        padding: "10px",
                                                        height: 57,
                                                        borderRadius: 8,
                                                    }}
                                                    variant="contained"
                                                    disableelevation="true"
                                                    color="primary"
                                                    onClick={
                                                        handleSearchProjects
                                                    }
                                                    disabled={!searchCC}
                                                >
                                                    <img
                                                        src="/img/search.svg"
                                                        alt="Filtro"
                                                        style={{
                                                            width: 25,
                                                            height: 25,
                                                        }}
                                                    />
                                                </Button>
                                            </View>
                                        </FieldContainer>

                                        <FieldContainer>
                                            <SubTitle>Status</SubTitle>
                                            <View style={{ gap: 8 }}>
                                                <Select
                                                    value={searchCCStatus}
                                                    onChange={
                                                        handleCCStatusChange
                                                    }
                                                    style={{
                                                        width: 180,
                                                    }}
                                                >
                                                    <MenuItem
                                                        key={1}
                                                        value="todos"
                                                    >
                                                        TODOS
                                                    </MenuItem>
                                                    <MenuItem
                                                        key={2}
                                                        value="ativo"
                                                    >
                                                        ATIVO
                                                    </MenuItem>
                                                    <MenuItem
                                                        key={3}
                                                        value="inativo"
                                                    >
                                                        INATIVO
                                                    </MenuItem>
                                                </Select>
                                            </View>
                                        </FieldContainer>
                                    </div>

                                    <ButtonAdd
                                        variant="text"
                                        onClick={() => {
                                            setOpenAddProject(true);
                                            setSelectedProfile(profile);
                                            if (!impersonateCompanyId) {
                                                setOpenAddProject(true);
                                                setSelectedProfile(profile);
                                            }
                                        }}
                                        disabled={!!impersonateCompanyId}
                                        style={{ alignSelf: "end" }}
                                    >
                                        <AddIcon />
                                        ADICIONAR CENTRO DE CUSTO
                                    </ButtonAdd>
                                </View>
                                <FieldContainer>
                                    {searchLoading ? (
                                        <View
                                            style={{
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: 100,
                                            }}
                                        >
                                            <CircularProgress size={24} />
                                        </View>
                                    ) : projects.length === 0 ? (
                                        <View
                                            style={{
                                                padding: "20px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <p>Nenhum resultado encontrado.</p>
                                        </View>
                                    ) : (
                                        <DataGrid
                                            disableRowSelectionOnClick
                                            rows={projects.map((project) => ({
                                                id: decodeId(project.node.id),
                                                name: project.node.name,
                                                scope: project.node
                                                    .restrictedProject
                                                    ? "RESTRITO"
                                                    : "TODOS",
                                                status: project.node.disabled
                                                    ? "INATIVO"
                                                    : "ATIVO",
                                                raw: project,
                                            }))}
                                            columns={[
                                                {
                                                    field: "id",
                                                    headerName: "ID",
                                                    flex: 1,
                                                    width: 100,
                                                },
                                                {
                                                    field: "name",
                                                    headerName: "Nome",
                                                    flex: 1,
                                                    width: 250,
                                                },
                                                {
                                                    field: "scope",
                                                    headerName: "Acesso",
                                                    flex: 1,
                                                    width: 150,
                                                },
                                                {
                                                    field: "status",
                                                    headerName: "Status",
                                                    flex: 1,
                                                    width: 120,
                                                },
                                                {
                                                    field: "actions",
                                                    headerName: "Editar",
                                                    width: 100,
                                                    disableExport: true,
                                                    renderCell: (params) => {
                                                        return impersonateCompanyId ===
                                                            null ? (
                                                            <Button
                                                                onClick={() =>
                                                                    handleOpenEdit(
                                                                        setSelectedProject(
                                                                            params
                                                                                .row
                                                                                .raw
                                                                        )
                                                                    )
                                                                }
                                                                variant="text"
                                                                startIcon={
                                                                    <img
                                                                        src="/img/pen_edit.svg"
                                                                        alt="Editar"
                                                                    />
                                                                }
                                                            />
                                                        ) : (
                                                            <Button
                                                                onClick={() =>
                                                                    handleOpenEdit(
                                                                        setSelectedProject(
                                                                            params
                                                                                .row
                                                                                .raw
                                                                        )
                                                                    )
                                                                }
                                                                variant="text"
                                                                startIcon={
                                                                    <img
                                                                        src="/img/eye_on.png"
                                                                        alt="Ver"
                                                                        width={
                                                                            "20px"
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        );
                                                    },
                                                },
                                            ]}
                                            loading={searchLoading}
                                            slots={{
                                                toolbar: () => (
                                                    <CustomExportCCMenu
                                                        projects={categories.map(
                                                            (project) => ({
                                                                id: decodeId(
                                                                    project.node
                                                                        .id
                                                                ),
                                                                name: project
                                                                    .node.name,
                                                                scope: project
                                                                    .node
                                                                    .restrictedCategory
                                                                    ? "RESTRITO"
                                                                    : "TODOS",
                                                                limite: project
                                                                    .node
                                                                    .limitValue
                                                                    ? formatValueToCsvXlsx(
                                                                          project
                                                                              .node
                                                                              .limitValue
                                                                      )
                                                                    : "SEM LIMITE",
                                                                status: project
                                                                    .node
                                                                    .disabled
                                                                    ? "INATIVO"
                                                                    : "ATIVO",
                                                            })
                                                        )}
                                                    />
                                                ),
                                            }}
                                        />
                                    )}
                                </FieldContainer>
                            </Collapse>
                        </Box>
                        <Button
                            onClick={toggleDropdown5}
                            variant="outlined"
                            style={{
                                border: "none",
                                marginTop: 15,
                                padding: 0,
                            }}
                        >
                            {isOpen5 ? (
                                <ImageIcon src="/img/arrow_up.svg" alt="" />
                            ) : (
                                <ImageIcon src="/img/arrow_down.svg" alt="" />
                            )}
                            CATEGORIA
                        </Button>
                        <Box
                            style={{
                                paddingBottom: 20,
                                border: "none",
                                borderBottom: "1px solid #E6E6E6",
                                width: "100%",
                                paddingTop: 10,
                                paddingLeft: 10,
                            }}
                        >
                            <Collapse in={isOpen5}>
                                <View
                                    style={{
                                        paddingTop: "24px",
                                        paddingBottom: "40px",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: 24,
                                        }}
                                    >
                                        <FieldContainer>
                                            <SubTitle>Nome</SubTitle>
                                            <View>
                                                <TextField
                                                    value={searchCategories}
                                                    variant="outlined"
                                                    style={{ borderRadius: 5 }}
                                                    onChange={(event) => {
                                                        setSearchCategory(
                                                            event.target.value
                                                        );
                                                    }}
                                                    onKeyUp={(e) => {
                                                        if (e.key === "Enter")
                                                            handleSearchCategories();
                                                    }}
                                                    InputProps={{
                                                        endAdornment:
                                                            searchCategories ? (
                                                                <IconButton
                                                                    style={{
                                                                        position:
                                                                            "absolute",
                                                                        right: 12,
                                                                    }}
                                                                    onClick={() =>
                                                                        handleClear(
                                                                            "category"
                                                                        )
                                                                    }
                                                                    edge="end"
                                                                >
                                                                    <ClearIcon />
                                                                </IconButton>
                                                            ) : null,
                                                    }}
                                                />
                                                <Button
                                                    style={{
                                                        marginLeft: 16,
                                                        fontSize: 12,
                                                        padding: "10px",
                                                        height: 57,
                                                        borderRadius: 8,
                                                    }}
                                                    variant="contained"
                                                    disableelevation="true"
                                                    color="primary"
                                                    onClick={
                                                        handleSearchCategories
                                                    }
                                                    disabled={!searchCategories}
                                                >
                                                    <img
                                                        src="/img/search.svg"
                                                        alt="Filtro"
                                                        style={{
                                                            width: 25,
                                                            height: 25,
                                                        }}
                                                    />
                                                </Button>
                                            </View>
                                        </FieldContainer>

                                        <FieldContainer>
                                            <SubTitle>Status</SubTitle>
                                            <View style={{ gap: 8 }}>
                                                <Select
                                                    value={searchCategoryStatus}
                                                    onChange={
                                                        handleCategoryStatusChange
                                                    }
                                                    style={{
                                                        width: 180,
                                                    }}
                                                >
                                                    <MenuItem
                                                        key={1}
                                                        value="todos"
                                                    >
                                                        TODOS
                                                    </MenuItem>
                                                    <MenuItem
                                                        key={2}
                                                        value="ativo"
                                                    >
                                                        ATIVO
                                                    </MenuItem>
                                                    <MenuItem
                                                        key={3}
                                                        value="inativo"
                                                    >
                                                        INATIVO
                                                    </MenuItem>
                                                </Select>
                                            </View>
                                        </FieldContainer>
                                    </div>
                                    <ButtonAdd
                                        variant="text"
                                        onClick={() => {
                                            if (!impersonateCompanyId) {
                                                setOpenAddCategory(true);
                                                setSelectedProfile(profile);
                                            }
                                        }}
                                        disabled={!!impersonateCompanyId}
                                        style={{ alignSelf: "end" }}
                                    >
                                        <AddIcon />
                                        ADICIONAR CATEGORIA
                                    </ButtonAdd>
                                </View>
                                <FieldContainer>
                                    {searchLoading ? (
                                        <View
                                            style={{
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: 100,
                                            }}
                                        >
                                            <CircularProgress size={24} />
                                        </View>
                                    ) : categories.length === 0 ? (
                                        <View
                                            style={{
                                                padding: "20px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <p>Nenhum resultado encontrado.</p>
                                        </View>
                                    ) : (
                                        <DataGrid
                                            disableRowSelectionOnClick
                                            rows={categories.map(
                                                (categorie, index) => ({
                                                    id: decodeId(
                                                        categorie.node.id
                                                    ),
                                                    name: categorie.node.name,
                                                    scope: categorie.node
                                                        .restrictedCategory
                                                        ? "RESTRITO"
                                                        : "TODOS",
                                                    limite: categorie.node
                                                        .limitValue
                                                        ? "R$ " +
                                                          parseFloat(
                                                              categorie.node
                                                                  .limitValue
                                                          )
                                                              .toFixed(2)
                                                              .replace(".", ",")
                                                        : "SEM LIMITE",
                                                    status: categorie.node
                                                        .disabled
                                                        ? "INATIVO"
                                                        : "ATIVO",
                                                    raw: categorie,
                                                })
                                            )}
                                            columns={[
                                                {
                                                    field: "id",
                                                    headerName: "ID",
                                                    flex: 1,
                                                    minWidth: 100,
                                                },
                                                {
                                                    field: "name",
                                                    headerName: "Nome",
                                                    flex: 1,
                                                    minWidth: 250,
                                                },
                                                {
                                                    field: "scope",
                                                    headerName: "Acesso",
                                                    flex: 1,
                                                    minWidth: 150,
                                                },
                                                {
                                                    field: "limite",
                                                    headerName: "Limite",
                                                    flex: 1,
                                                    minWidth: 150,
                                                },
                                                {
                                                    field: "status",
                                                    headerName: "Status",
                                                    flex: 1,
                                                    minWidth: 120,
                                                },
                                                {
                                                    field: "actions",
                                                    headerName: "Editar",
                                                    disableExport: true,
                                                    renderCell: (params) => {
                                                        return impersonateCompanyId ===
                                                            null ? (
                                                            <Button
                                                                onClick={() =>
                                                                    handleOpenEdit(
                                                                        setselectedCategory(
                                                                            params
                                                                                .row
                                                                                .raw
                                                                        )
                                                                    )
                                                                }
                                                                variant="text"
                                                                startIcon={
                                                                    <img
                                                                        src="/img/pen_edit.svg"
                                                                        alt="Editar"
                                                                    />
                                                                }
                                                            />
                                                        ) : (
                                                            <Button
                                                                onClick={() =>
                                                                    handleOpenEdit(
                                                                        setselectedCategory(
                                                                            params
                                                                                .row
                                                                                .raw
                                                                        )
                                                                    )
                                                                }
                                                                variant="text"
                                                                startIcon={
                                                                    <img
                                                                        src="/img/eye_on.png"
                                                                        alt="Ver"
                                                                        width={
                                                                            "20px"
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        );
                                                    },
                                                },
                                            ]}
                                            slots={{
                                                toolbar: () => (
                                                    <CustomExportCategoriesMenu
                                                        categories={categories.map(
                                                            (categorie) => ({
                                                                id: decodeId(
                                                                    categorie
                                                                        .node.id
                                                                ),
                                                                name: categorie
                                                                    .node.name,
                                                                scope: categorie
                                                                    .node
                                                                    .restrictedCategory
                                                                    ? "RESTRITO"
                                                                    : "TODOS",
                                                                limite: categorie
                                                                    .node
                                                                    .limitValue
                                                                    ? formatValueToCsvXlsx(
                                                                          categorie
                                                                              .node
                                                                              .limitValue
                                                                      )
                                                                    : "SEM LIMITE",
                                                                status: categorie
                                                                    .node
                                                                    .disabled
                                                                    ? "INATIVO"
                                                                    : "ATIVO",
                                                            })
                                                        )}
                                                    />
                                                ),
                                            }}
                                        />
                                    )}
                                </FieldContainer>
                            </Collapse>
                        </Box>
                        <Button
                            onClick={toggleDropdown6}
                            variant="outlined"
                            style={{
                                border: "none",
                                marginTop: 15,
                                padding: 0,
                            }}
                        >
                            {isOpen6 ? (
                                <ImageIcon src="/img/arrow_up.svg" alt="" />
                            ) : (
                                <ImageIcon src="/img/arrow_down.svg" alt="" />
                            )}
                            API
                        </Button>
                        <Box
                            style={{
                                paddingBottom: 20,
                                border: "none",
                                borderBottom: "1px solid #E6E6E6",
                                width: "100%",
                                paddingTop: 10,
                                paddingLeft: 10,
                            }}
                        >
                            <Collapse in={isOpen6}>
                                <Field
                                    style={{
                                        width: "70%",
                                        marginLeft: 30,
                                        flexDirection: "column",
                                    }}
                                >
                                    <TextApi style={{ marginBottom: 30 }}>
                                        Quer obter acesso a todos os recursos
                                        disponíveis em nossa API?{" "}
                                        <strong>É muito simples!</strong>
                                    </TextApi>
                                    <FieldContainer
                                        style={{ marginLeft: 20, width: 888 }}
                                    >
                                        <TextApi>
                                            - Solicite seu acesso encaminhando
                                            uma mensagem através do e-mail
                                            <a
                                                style={{
                                                    color: "#57C4C2",
                                                    fontWeight: 600,
                                                }}
                                                href="mailto:oi@zagapp.com.br"
                                            >
                                                {" "}
                                                oi@zagapp.com.br
                                            </a>
                                            ;
                                        </TextApi>
                                        <TextApi>
                                            - Escreva no assunto{" "}
                                            <strong>“API”</strong>;
                                        </TextApi>
                                        <TextApi>
                                            - Aguarde que nosso time retornará a
                                            mensagem com seu ID e Secret para
                                            autenticação no Portal do
                                            Desenvolvedor que pode ser acessado
                                            neste link:{" "}
                                            <a
                                                href="https://portaldev.zagapp.com.br/"
                                                target="blank"
                                            >
                                                <strong
                                                    style={{
                                                        color: "#57C4C2",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {" "}
                                                    https://portaldev.zagapp.com.br/
                                                </strong>
                                            </a>
                                        </TextApi>
                                    </FieldContainer>
                                    <TextApi style={{ marginTop: 30 }}>
                                        <strong>Lembre-se:</strong> Este recurso
                                        está disponível apenas para clientes
                                        pagantes.
                                    </TextApi>
                                </Field>
                            </Collapse>
                        </Box>
                        {/* <FieldContainer>
              <View style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", marginTop: 30, marginBottom: 50 }} >
                <Button style={{ color: "#DE6061" }}>
                  DESATIVAR EMPRESA
                </Button>
                <Button style={{ color: "#181122" }}>
                  EXPORTAR DADOS
                </Button>
              </View>
            </FieldContainer> */}
                    </Grid>
                </Content>
                <ErrorModal
                    open={errorModalOpen}
                    onClose={() => setErrorModalOpen(false)}
                    message={errorModalMessage}
                />

                {/* Modal de Confirmação de Plano ou Cancelamento */}
                {openPlanModal && selectedPlan && (
                    <ModalOverlay
                        onClick={() => {
                            setOpenPlanModal(false);
                            setIsCancelMode(false);
                            setAcceptedTerms(false);
                            setPlanCnpj("");
                            setPlanCnpjTouched(false);
                            setPlanCnpjError("");
                        }}
                    >
                        <ModalContainer onClick={(e) => e.stopPropagation()}>
                            <HeaderModalPlans>
                                <BasicTitle
                                    title={
                                        isCancelMode
                                            ? "Confirmar cancelamento"
                                            : "Confirmar escolha"
                                    }
                                />
                                <ModalClose
                                    src="/img/modal_close.svg"
                                    onClick={() => setOpenPlanModal(false)}
                                />
                            </HeaderModalPlans>
                            <PlanSummaryBox>
                                <PlanTitle>
                                    {selectedPlan.price === 0
                                        ? selectedPlan.name
                                        : `R$ ${selectedPlan.price
                                              .toFixed(2)
                                              .replace(".", ",")}`}
                                </PlanTitle>
                                <PlanSubtitle>
                                    {selectedPlan.description}
                                </PlanSubtitle>
                                <ActiveUsersText>
                                    Usuários ativos atualmente:{" "}
                                    {activeUsersCount}
                                </ActiveUsersText>
                                {!isCancelMode && selectedPlan.id !== "free" && (
                                    <>
                                        <DividerLine />
                                        <BillingInfoText>
                                            Sua próxima fatura já virá com o
                                            valor atualizado.
                                        </BillingInfoText>
                                    </>
                                )}
                            </PlanSummaryBox>

                            {isCancelMode ? (
                                <CancelWarningText>
                                    Cancelando seu plano, você ainda poderá ter{" "}
                                    {selectedPlan?.maxUsers} usuários até o dia{" "}
                                    {subscriptionNextDueDate
                                        ? new Date(
                                              subscriptionNextDueDate
                                          ).toLocaleDateString("pt-BR")
                                        : "do próximo pagamento"}
                                    .
                                </CancelWarningText>
                            ) : (
                                <>
                                    {normalizeCnpj(companyCnpj).length ===
                                        0 && (
                                        <Box
                                            style={{
                                                width: "100%",
                                                marginTop: 16,
                                                marginBottom: 10,
                                            }}
                                        >
                                            <TextField
                                                label="CNPJ da empresa *"
                                                variant="outlined"
                                                value={formatCnpj(planCnpj)}
                                                onChange={(e) => {
                                                    const digits =
                                                        normalizeCnpj(
                                                            e.target.value
                                                        ).slice(0, 14);
                                                    setPlanCnpj(digits);
                                                    setPlanCnpjTouched(true);
                                                    setPlanCnpjError(
                                                        getCnpjError(digits)
                                                    );
                                                }}
                                                onKeyDown={handleCnpjKeyDown}
                                                inputProps={{
                                                    inputMode: "numeric",
                                                    maxLength: 18,
                                                }}
                                                error={
                                                    planCnpjTouched &&
                                                    !!planCnpjError
                                                }
                                                helperText={
                                                    planCnpjTouched
                                                        ? planCnpjError
                                                        : ""
                                                }
                                                style={{ width: "100%" }}
                                            />
                                        </Box>
                                    )}

                                    {selectedPlan.id !== "free" && (
                                        <FormControlLabel
                                            defaultChecked={acceptedTerms}
                                            control={
                                                <Checkbox
                                                    sx={{
                                                        p: 0,
                                                        ml: 0,
                                                        mr: 1,
                                                    }}
                                                    checked={acceptedTerms}
                                                    defaultChecked={acceptedTerms}
                                                    onChange={(e) =>
                                                        setAcceptedTerms(
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                <TermsLabel>
                                                    Li e aceito os{" "}
                                                    <TermsLink
                                                        href="https://zagapp.com.br/termos-de-uso"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        termos de uso
                                                    </TermsLink>
                                                    .
                                                </TermsLabel>
                                            }
                                        />
                                    )}
                                </>
                            )}

                            {isCancelMode ? (
                                <CancelButton
                                    disabled={loading}
                                    onClick={handleCancelSubscription}
                                >
                                    {loading ? "Processando..." : "Cancelar"}
                                </CancelButton>
                            ) : (
                                <ModalButtonsRow>
                                    <LinkButton
                                        onClick={() => {
                                            setOpenPlanModal(false);
                                            setAcceptedTerms(false);
                                            setPlanCnpj("");
                                            setPlanCnpjTouched(false);
                                            setPlanCnpjError("");
                                        }}
                                    >
                                        Escolher outro plano
                                    </LinkButton>
                                    <span style={{ cursor: !acceptedTerms ? 'not-allowed' : 'pointer' }}>
                                        <PrimaryButton
                                            disabled={
                                                !acceptedTerms ||
                                                loading ||
                                                (normalizeCnpj(companyCnpj)
                                                    .length === 0 &&
                                                    !!getCnpjError(planCnpj))
                                            }
                                            onClick={handleConfirmPlan}
                                        >
                                            {loading
                                                ? "Processando..."
                                                : "Confirmar escolha"}
                                        </PrimaryButton>
                                    </span>
                                </ModalButtonsRow>
                            )}
                        </ModalContainer>
                    </ModalOverlay>
                )}

                {/* Toast de Sucesso/Erro */}
                {showToast && (
                    <ToastContainer>
                        {toastType === "success" ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM13.857 8.191C13.9149 8.11129 13.9566 8.02095 13.9796 7.92514C14.0026 7.82933 14.0065 7.72994 13.991 7.63262C13.9756 7.5353 13.9412 7.44198 13.8897 7.35797C13.8382 7.27396 13.7707 7.20091 13.691 7.143C13.6113 7.08509 13.5209 7.04344 13.4251 7.02044C13.3293 6.99744 13.2299 6.99354 13.1326 7.00895C13.0353 7.02437 12.942 7.0588 12.858 7.11028C12.774 7.16176 12.7009 7.22929 12.643 7.309L9.16 12.099L7.28 10.219C7.21078 10.1474 7.128 10.0903 7.03647 10.051C6.94495 10.0118 6.84653 9.99114 6.74694 9.99032C6.64736 9.9895 6.54861 10.0085 6.45646 10.0463C6.3643 10.084 6.28059 10.1398 6.2102 10.2102C6.13982 10.2807 6.08417 10.3644 6.0465 10.4566C6.00883 10.5488 5.9899 10.6476 5.99081 10.7472C5.99173 10.8467 6.01246 10.9451 6.05181 11.0366C6.09116 11.1281 6.14834 11.2108 6.22 11.28L8.72 13.78C8.79663 13.8567 8.88896 13.9158 8.99065 13.9534C9.09233 13.9909 9.20094 14.006 9.30901 13.9975C9.41708 13.9891 9.52203 13.9573 9.61663 13.9044C9.71123 13.8515 9.79324 13.7787 9.857 13.691L13.857 8.191Z"
                                    fill="#73CECC"
                                />
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM8.707 7.293C8.5184 7.11084 8.2658 7.01005 8.0036 7.01233C7.7414 7.01461 7.49059 7.11977 7.30518 7.30518C7.11977 7.49059 7.01461 7.7414 7.01233 8.0036C7.01005 8.2658 7.11084 8.5184 7.293 8.707L8.586 10L7.293 11.293C7.19749 11.3852 7.12131 11.4956 7.0689 11.6176C7.01649 11.7396 6.9889 11.8708 6.98775 12.0036C6.9866 12.1364 7.0119 12.2681 7.06218 12.391C7.11246 12.5139 7.18671 12.6255 7.2806 12.7194C7.3745 12.8133 7.48615 12.8875 7.60905 12.9378C7.73194 12.9881 7.86362 13.0134 7.9964 13.0123C8.12918 13.0111 8.2604 12.9835 8.38240 12.9311C8.50441 12.8787 8.61475 12.8025 8.707 12.707L10 11.414L11.293 12.707C11.4816 12.8892 11.7342 12.99 11.9964 12.9877C12.2586 12.9854 12.5094 12.8802 12.6948 12.6948C12.8802 12.5094 12.9854 12.2586 12.9877 11.9964C12.99 11.7342 12.8892 11.4816 12.707 11.293L11.414 10L12.707 8.707C12.8892 8.5184 12.99 8.2658 12.9877 8.0036C12.9854 7.7414 12.8802 7.49059 12.6948 7.30518C12.5094 7.11977 12.2586 7.01461 11.9964 7.01233C11.7342 7.01005 11.4816 7.11084 11.293 7.293L10 8.586L8.707 7.293Z"
                                    fill="#DE6061"
                                />
                            </svg>
                        )}
                        <ToastMessage>{toastMessage}</ToastMessage>
                    </ToastContainer>
                )}
            </Holder>
        </>
    );
}
